// Month grid logic for the calendar (pure functions, local time, weeks start on Monday).
import { eventEnd, ymd } from './domain';

export interface Month {
  y: number;
  m: number; // 0–11
}

const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const sod = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** All days shown for a month: full weeks Monday–Sunday (4–6 rows). */
export function monthGrid({ y, m }: Month): Date[] {
  const first = new Date(y, m, 1);
  const offset = (first.getDay() + 6) % 7; // days before the 1st back to Monday
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = Math.ceil((offset + daysInMonth) / 7) * 7;
  const start = addDays(first, -offset);
  return Array.from({ length: cells }, (_, i) => addDays(start, i));
}

/** Time range to load for the visible grid: from the first shown day 00:00 to the day after the last one. */
export function gridRange(month: Month) {
  const days = monthGrid(month);
  return { from: days[0], to: addDays(days[days.length - 1], 1) };
}

export const shiftMonth = ({ y, m }: Month, delta: number): Month => {
  const d = new Date(y, m + delta, 1);
  return { y: d.getFullYear(), m: d.getMonth() };
};

/** "2026-11" ↔ month; invalid input falls back to the month of `fallback`. */
export function parseMonth(s: string | null, fallback = new Date()): Month {
  const r = /^(\d{4})-(\d{2})$/.exec(s ?? '');
  const m = r ? Number(r[2]) - 1 : -1;
  const y = r ? Number(r[1]) : 0;
  if (r && m >= 0 && m <= 11 && y >= 1990 && y <= 2100) return { y, m };
  return { y: fallback.getFullYear(), m: fallback.getMonth() };
}
export const monthKey = ({ y, m }: Month) => `${y}-${String(m + 1).padStart(2, '0')}`;

export function parseDay(s: string | null): Date | null {
  const r = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? '');
  if (!r) return null;
  const d = new Date(Number(r[1]), Number(r[2]) - 1, Number(r[3]));
  return ymd(d) === s ? d : null; // rejects 2026-02-31
}

type Timed = { starts_at: string; ends_at: string | null; duration_min: number | null };

/** Events per day (key yyyy-mm-dd); multi-day events appear on every day they cover, sorted by start. */
export function dayMap<E extends Timed>(events: E[]): Map<string, E[]> {
  const map = new Map<string, E[]>();
  const sorted = [...events].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  for (const e of sorted) {
    const start = sod(new Date(e.starts_at));
    // an end at exactly midnight belongs to the previous day
    const endTime = eventEnd(e);
    const last = sod(new Date(Math.max(start.getTime(), endTime.getTime() - 1)));
    for (let d = start, guard = 0; d <= last && guard < 400; d = addDays(d, 1), guard++) {
      const k = ymd(d);
      const list = map.get(k);
      if (list) list.push(e);
      else map.set(k, [e]);
    }
  }
  return map;
}

/** True when the event starts on the given day (otherwise it continues from an earlier day). */
export const startsOn = (e: Timed, day: Date) => ymd(new Date(e.starts_at)) === ymd(day);
